import React, { useState, useEffect } from "react";
import {
    Button,
    Typography,
    TextField,
    Box,
    Autocomplete,
    Grid2 as Grid,
    FormControlLabel,
    Switch,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    OutlinedInput,
    ListItemText,
} from "@mui/material";
import { useDropzone } from "react-dropzone";
import { useFormik } from "formik";
import * as yup from "yup";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { ToastContainer } from "react-toastify";
import MapField from "../../common/MapField";
import TextEditor from "../../common/TextEditor";
import axios from "axios";
import { closestCenter, DndContext, DragEndEvent, DragOverlay, DragStartEvent, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { SortableItem } from "../../common/SortableItems";
import countriesData from "../../common/api/countryStates";
import { showErrorToast } from "../../common/util/util";
import { useCountryParam } from "../../common/util/useCountryParam";
import config from "../../common/Config";

const validationSchema = yup.object({
    title: yup.string().required("Title is required"),
    price: yup.string().required("Price is required"),
    address: yup.string().required("Address is required"),
    coordinates: yup
        .object()
        .shape({
            latitude: yup.number().required("Latitude is required"),
            longitude: yup.number().required("Longitude is required"),
        })
        .required("Map location is required"),
    description: yup.string().required("Description is required"),
    images: yup
        .array()
        .min(1, "At least one image is required")
        .required("Images are required"),
    propertyStatus: yup.string().required("Property status is required"),
    featured: yup.boolean(),
});

const fetchSuggestions = async (query: any) => {
    if (!query) return [];
    try {
        const response = await axios.get(
            `https://nominatim.openstreetmap.org/search`, {
            params: {
                q: query,
                format: "json",
                addressdetails: 1,
                limit: 5,
            }
        }
        );
        return response.data;
    } catch (error) {
        console.error("Error fetching suggestions:", error);
        return [];
    }
};

const AddPropertyIndia = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const country = useCountryParam() || searchParams.get('country') || 'india'; // Ensure we have country parameter
    const id = useParams(); // Get the entire params object like UK version
    const queryId = searchParams.get('id'); // For query parameter version
    const actualId = queryId || id.id; // Use query ID if available, otherwise URL ID

    const [suggestions, setSuggestions] = useState([]);
    const [activeId, setActiveId] = useState<any>(null);
    const [propertyType, setPropertyType] = useState<string>(searchParams.get('type')?.split('?')[0] || 'Residential');

    // Property type configurations
    const propertyTypeConfig: Record<string, any> = {
        Residential: {
            endpoint: `${config.baseUrl}/api/residential-properties`,
            propertyTypes: ['Apartment', 'Villa', 'Independent House', 'Builder Floor', 'Penthouse', 'Plot'],
            bhkTypes: ['1 RK', '1 BHK', '2 BHK', '3 BHK', '4 BHK', '5 BHK', '5+ BHK'],
            facings: ['North', 'South', 'East', 'West', 'North-East', 'North-West', 'South-East', 'South-West'],
            furnishedTypes: ['Fully Furnished', 'Semi Furnished', 'Unfurnished'],
            amenities: ['Swimming Pool', 'Gym', 'Security', 'Power Backup', 'Lift', 'Garden', 'Children Play Area', 'Parking', 'Water Supply', 'Clubhouse'],
            areaUnits: ['Sq. Yards', 'Sq. Feet', 'Guntas', 'Acres', 'Gadulu']
        },
        Commercial: {
            endpoint: `${config.baseUrl}/api/commercial-properties`,
            propertyTypes: ['Office Space', 'Retail Shop', 'Showroom', 'Warehouse', 'Industrial Land', 'Commercial Land', 'Restaurant Space', 'Hotel', 'Hospital', 'School', 'Shopping Mall', 'Multiplex', 'Petrol Pump', 'Factory', 'Cold Storage', 'Plot'],
            facings: ['North', 'South', 'East', 'West', 'North-East', 'North-West', 'South-East', 'South-West'],
            furnishedTypes: ['Fully Furnished', 'Semi Furnished', 'Unfurnished'],
            idealFor: ['Office', 'Shop', 'Showroom', 'Godown/Warehouse', 'Industrial Shed', 'Restaurant', 'Bank', 'ATM', 'Clinic', 'Gym', 'Salon', 'Coaching Center'],
            amenities: ['Lift', 'Escalator', 'Power Backup', 'Security', 'CCTV', 'Fire Safety', 'Air Conditioning', 'Cafeteria', 'Conference Room', 'Reception Area', 'Parking', 'Washrooms', 'Pantry', 'Internet/WiFi', 'Intercom', 'Visitor Parking', 'Disabled Friendly', 'Service Lift', 'Water Storage', 'Waste Disposal', 'Maintenance Staff', 'Cleaning Service', '24 Hour Water Supply'],
            areaUnits: ['Sq. Yards', 'Sq. Feet', 'Guntas', 'Acres', 'Gadulu']
        },
        Agricultural: {
            endpoint: `${config.baseUrl}/api/agricultural-lands`,
            areaUnits: ['Sq. Yards', 'Sq. Feet', 'Guntas', 'Acres', 'Gadulu']
        }
    };

    const configData = propertyTypeConfig[propertyType];

    const getInitialValues = () => {
        const baseValues = {
            title: "",
            price: "",
            description: "",
            address: "",
            coordinates: {
                latitude: 28.6139,
                longitude: 77.2090,
            },
            images: [],
            status: "Active",
            featured: false,
            postedBy: "Owner",
            listingType: "Sale",
            propertyStatus: propertyType === 'Residential' ? 'Available' : (propertyType === 'Agricultural' ? 'For Rent' : 'For Sale'),
        };

        // Add property type specific fields
        switch (propertyType) {
            case 'Residential':
                return {
                    ...baseValues,
                    propertyType: "",
                    bhkType: "",
                    bedrooms: 0,
                    bathrooms: 0,
                    furnished: "",
                    facing: "",
                    area: { value: 0, unit: "Sq. Yards" },
                    amenities: [],
                    floorPlan: "",
                    eceDocument: "",
                };
            case 'Commercial':
                return {
                    ...baseValues,
                    propertyType: "",
                    facing: "",
                    area: { value: 0, unit: "Sq. Yards" },
                    floorNumber: 0,
                    furnished: "",
                    idealFor: [],
                    amenities: [],
                    floorPlan: "",
                    eceDocument: "",
                };
            case 'Agricultural':
                return {
                    ...baseValues,
                    area: { value: 0, unit: "Acres" },
                };
            default:
                return baseValues;
        }
    };

    const handleSearchInput = async (e: any) => {
        const value = e.target.value;
        formik.setFieldValue('address', value);
        const results = await fetchSuggestions(value);
        setSuggestions(results);
    };

    const handleSuggestionClick = (location: any) => {
        if (location && location.display_name && location.lat && location.lon) {
            formik.setFieldValue('address', location.display_name);
            formik.setFieldValue('coordinates', {
                latitude: parseFloat(location.lat),
                longitude: parseFloat(location.lon)
            });
            setSuggestions([]);
        }
    };

    const formik: any = useFormik({
        initialValues: getInitialValues(),
        validationSchema,
        onSubmit: async (values: any) => {
            try {
                const formData = new FormData();

                // Handle different field structures for different property types
                Object.keys(values).forEach(key => {
                    if (key === 'images') {
                        values[key]?.forEach((image: any) => {
                            if (typeof image === "string") {
                                formData.append("existingImages", image);
                            } else if (image?.file instanceof File) {
                                formData.append("images", image.file);
                            }
                        });
                    } else if (key === 'floorPlan' || key === 'eceDocument') {
                        // Handle file uploads for floorPlan and eceDocument
                        if (values[key] instanceof File) {
                            formData.append(key, values[key]);
                        } else if (typeof values[key] === 'string' && values[key]) {
                            // For existing files during edit
                            formData.append(`existing${key.charAt(0).toUpperCase() + key.slice(1)}`, values[key]);
                        }
                    } else if (key === 'coordinates' || key === 'area' || key === 'amenities' || key === 'idealFor') {
                        formData.append(key, JSON.stringify(values[key]));
                    } else if (values[key] !== undefined && values[key] !== null && values[key] !== '') {
                        formData.append(key, values[key]);
                    }
                });

                const url = actualId ? `${configData.endpoint}/${actualId}` : configData.endpoint;
                const method = actualId ? 'PUT' : 'POST';

                // Debug: Log what's being sent
                console.log('Submitting to:', url);
                console.log('Method:', method);
                console.log('Property Type:', propertyType);
                console.log('Form Values:', values);

                // Log FormData contents (compatible with older TypeScript)
                const formDataEntries: string[] = [];
                formData.forEach((value, key) => {
                    formDataEntries.push(`${key}: ${value}`);
                });
                console.log('FormData contents:', formDataEntries);

                const response = await fetch(url, {
                    method,
                    body: formData,
                });

                if (response.ok) {
                    console.log('Save successful, redirecting to:', `/properties?country=${country}&type=${propertyType}`);
                    navigate(`/properties?country=${country}&type=${propertyType}`);
                } else {
                    const errorData = await response.json();
                    console.error('API Error:', errorData);
                    throw new Error(errorData.message || 'Failed to save property');
                }
            } catch (error: any) {
                console.error("Error submitting form:", error);
                showErrorToast(error.message || "Failed to save property");
                if (error.message?.includes("Unexpected field")) {
                    showErrorToast("You can only upload up to 10 images at a time");
                }
            }
        },
    });

    const handleLocationChange = (coordinates: any, address?: string) => {
        if (coordinates && coordinates.lat && coordinates.lng) {
            // Convert from MapField format (lat, lng) to backend format (latitude, longitude)
            formik.setFieldValue("coordinates", {
                latitude: coordinates.lat,
                longitude: coordinates.lng
            });
            if (address) {
                formik.setFieldValue("address", address);
            }
        }
    };

    const onDrop = (acceptedFiles: any) => {
        const newImages = acceptedFiles.map((file: File) => ({
            preview: URL.createObjectURL(file),
            file,
        }));
        formik.setFieldValue("images", [...(formik.values.images || []), ...newImages]);
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: { "image/*": [".png", ".jpg", ".jpeg"] },
    });

    const removeImage = (index: any) => {
        const updatedImages = formik.values.images?.filter((_: any, i: any) => i !== index);
        formik.setFieldValue("images", updatedImages);
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (over && active.id !== over.id) {
            const oldIndex = formik.values.images?.findIndex((image: any) => image.preview === active.id);
            const newIndex = formik.values.images?.findIndex((image: any) => image.preview === over.id);
            const reorderedImages = arrayMove(formik.values.images, oldIndex, newIndex);
            formik.setFieldValue("images", reorderedImages);
        }
        setActiveId(null);
    };

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor)
    );

    // Function to detect property type from loaded data
    const detectPropertyType = (data: any) => {
        if (data.bhkType || data.bedrooms || data.bathrooms) {
            return 'Residential';
        } else if (data.floorNumber !== undefined || data.idealFor) {
            return 'Commercial';
        } else if (data.area && typeof data.area === 'object' && data.area.unit) {
            return 'Agricultural';
        }
        return 'Residential'; // default
    };

    // Load existing property data for editing - Fixed to prevent infinite loop
    useEffect(() => {
        if (actualId) {
            // Try all endpoints to find the property
            const endpoints = [
                { type: 'Residential', endpoint: `${config.baseUrl}/api/residential-properties` },
                { type: 'Commercial', endpoint: `${config.baseUrl}/api/commercial-properties` },
                { type: 'Agricultural', endpoint: `${config.baseUrl}/api/agricultural-lands` }
            ];

            const tryEndpoint = async (endpointInfo: any) => {
                try {
                    const response = await fetch(`${endpointInfo.endpoint}/${actualId}`);
                    if (response.ok) {
                        const data = await response.json();
                        console.log('Property data loaded:', data);
                        setPropertyType(endpointInfo.type); // Use the type from the successful endpoint
                        formik.setValues(data);
                        return true;
                    }
                    return false;
                } catch (error) {
                    return false;
                }
            };

            // Try endpoints in order
            const loadProperty = async () => {
                for (const endpointInfo of endpoints) {
                    const success = await tryEndpoint(endpointInfo);
                    if (success) break;
                }
            };

            loadProperty();
        }
    }, [actualId]); // Only depend on actualId

    const renderPropertyTypeFields = () => {
        switch (propertyType) {
            case 'Residential':
                return (
                    <>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <label htmlFor="propertyType" className="form-label">Property Type</label>
                            <Autocomplete
                                options={configData.propertyTypes}
                                value={formik.values.propertyType}
                                onChange={(event, value) => formik.setFieldValue("propertyType", value)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="Select Property Type"
                                        error={formik.touched.propertyType && Boolean(formik.errors.propertyType)}
                                        helperText={formik.touched.propertyType && formik.errors.propertyType}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                            <label htmlFor="bhkType" className="form-label">BHK Type</label>
                            <Autocomplete
                                options={configData.bhkTypes}
                                value={formik.values.bhkType}
                                disabled={formik.values.propertyType === 'Plot' || formik.values.propertyType === 'Builder Floor'}
                                onChange={(event, value) => formik.setFieldValue("bhkType", value)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="Select BHK Type"
                                        error={formik.touched.bhkType && Boolean(formik.errors.bhkType)}
                                        helperText={formik.touched.bhkType && formik.errors.bhkType}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                            <label htmlFor="bedrooms" className="form-label">Bedrooms</label>
                            <TextField
                                fullWidth
                                name="bedrooms"
                                type="number"
                                disabled={formik.values.propertyType === 'Plot' || formik.values.propertyType === 'Builder Floor'}
                                value={formik.values.bedrooms}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.bedrooms && Boolean(formik.errors.bedrooms)}
                                helperText={formik.touched.bedrooms && formik.errors.bedrooms}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                            <label htmlFor="bathrooms" className="form-label">Bathrooms</label>
                            <TextField
                                fullWidth
                                name="bathrooms"
                                type="number"
                                disabled={formik.values.propertyType === 'Plot' || formik.values.propertyType === 'Builder Floor'}
                                value={formik.values.bathrooms}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.bathrooms && Boolean(formik.errors.bathrooms)}
                                helperText={formik.touched.bathrooms && formik.errors.bathrooms}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                            <label htmlFor="facing" className="form-label">Facing</label>
                            <Autocomplete
                                options={configData.facings || []}
                                value={formik.values.facing}
                                onChange={(event, value) => formik.setFieldValue("facing", value)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="Select Facing"
                                        error={formik.touched.facing && Boolean(formik.errors.facing)}
                                        helperText={formik.touched.facing && formik.errors.facing}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                            <label htmlFor="furnished" className="form-label">Furnished</label>
                            <Autocomplete
                                options={configData.furnishedTypes}
                                value={formik.values.furnished}
                                disabled={formik.values.propertyType === 'Plot' || formik.values.propertyType === 'Builder Floor'}
                                onChange={(event, value) => formik.setFieldValue("furnished", value)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="Select Furnished Type"
                                        error={formik.touched.furnished && Boolean(formik.errors.furnished)}
                                        helperText={formik.touched.furnished && formik.errors.furnished}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                            <label htmlFor="areaValue" className="form-label">Area Value</label>
                            <TextField
                                fullWidth
                                name="areaValue"
                                type="number"
                                value={formik.values.area?.value ?? ''}
                                onChange={(e) => formik.setFieldValue("area", {
                                    ...formik.values.area,
                                    value: e.target.value === '' ? '' : Number(e.target.value)
                                })}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                            <label htmlFor="areaUnit" className="form-label">Area Unit</label>
                            <Autocomplete
                                options={configData.areaUnits}
                                value={formik.values.area?.unit || "Sq. Yards"}
                                onChange={(event, value) => formik.setFieldValue("area", {
                                    ...formik.values.area,
                                    unit: value
                                })}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="Select Area Unit"
                                    />
                                )}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 12 }}>
                            <label htmlFor="amenities" className="form-label">Amenities</label>
                            <Autocomplete
                                multiple
                                options={configData.amenities}
                                value={formik.values.amenities || []}
                                onChange={(event, value) => formik.setFieldValue("amenities", value)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="Select Amenities"
                                    />
                                )}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                            <label htmlFor="floorPlan" className="form-label">Floor Plan</label>
                            <TextField
                                type="file"
                                name="floorPlan"
                                id="floorPlan"
                                onChange={(event) => {
                                    const target = event.target as HTMLInputElement;
                                    const file = target.files?.[0];
                                    formik.setFieldValue("floorPlan", file);
                                }}
                                variant="outlined"
                                fullWidth
                            />
                            {formik.values.floorPlan ? (
                                <div style={{ marginTop: "10px" }}>
                                    <img
                                        src={id ? formik.values.floorPlan : URL.createObjectURL(formik.values.floorPlan)}
                                        alt="floorplan"
                                        style={{ maxWidth: "200px", maxHeight: "200px" }}
                                    />
                                </div>
                            ) : null}
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                            <label htmlFor="eceDocument" className="form-label">ECE Documents</label>
                            <TextField
                                type="file"
                                name="eceDocument"
                                id="eceDocument"
                                onChange={(event) => {
                                    const target = event.target as HTMLInputElement;
                                    const file = target.files?.[0];
                                    formik.setFieldValue("eceDocument", file);
                                }}
                                inputProps={{ accept: ".pdf,.doc,.docx" }}
                                variant="outlined"
                                fullWidth
                            />
                            {formik.values.eceDocument ? (
                                <div style={{ marginTop: "10px" }}>
                                    {typeof formik.values.eceDocument === "string" ? (
                                        <a
                                            href={formik.values.eceDocument}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ color: "#1976d2", textDecoration: "underline" }}
                                        >
                                            View Uploaded Document
                                        </a>
                                    ) : (
                                        <a
                                            href={URL.createObjectURL(formik.values.eceDocument)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ color: "#1976d2", textDecoration: "underline" }}
                                        >
                                            {formik.values.eceDocument.name}
                                        </a>
                                    )}
                                </div>
                            ) : null}
                        </Grid>
                    </>
                );

            case 'Commercial':
                return (
                    <>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <label htmlFor="propertyType" className="form-label">Property Type</label>
                            <Autocomplete
                                options={configData.propertyTypes}
                                value={formik.values.propertyType}
                                onChange={(event, value) => formik.setFieldValue("propertyType", value)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="Select Property Type"
                                        error={formik.touched.propertyType && Boolean(formik.errors.propertyType)}
                                        helperText={formik.touched.propertyType && formik.errors.propertyType}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                            <label htmlFor="areaValue" className="form-label">Area Value</label>
                            <TextField
                                fullWidth
                                name="areaValue"
                                type="number"
                                value={formik.values.area?.value ?? ''}
                                onChange={(e) => formik.setFieldValue("area", {
                                    ...formik.values.area,
                                    value: e.target.value === '' ? '' : Number(e.target.value)
                                })}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                            <label htmlFor="areaUnit" className="form-label">Area Unit</label>
                            <Autocomplete
                                options={configData.areaUnits}
                                value={formik.values.area?.unit || "Sq. Yards"}
                                onChange={(event, value) => formik.setFieldValue("area", {
                                    ...formik.values.area,
                                    unit: value
                                })}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="Select Area Unit"
                                    />
                                )}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                            <label htmlFor="floorNumber" className="form-label">Floor Number</label>
                            <TextField
                                fullWidth
                                name="floorNumber"
                                type="number"
                                value={formik.values.floorNumber}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                            <label htmlFor="facing" className="form-label">Facing</label>
                            <Autocomplete
                                options={configData.facings || []}
                                value={formik.values.facing}
                                onChange={(event, value) => formik.setFieldValue("facing", value)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="Select Facing"
                                        error={formik.touched.facing && Boolean(formik.errors.facing)}
                                        helperText={formik.touched.facing && formik.errors.facing}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                            <label htmlFor="furnished" className="form-label">Furnished</label>
                            <Autocomplete
                                options={configData.furnishedTypes}
                                value={formik.values.furnished}
                                onChange={(event, value) => formik.setFieldValue("furnished", value)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="Select Furnished Type"
                                        error={formik.touched.furnished && Boolean(formik.errors.furnished)}
                                        helperText={formik.touched.furnished && formik.errors.furnished}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 12 }}>
                            <label htmlFor="idealFor" className="form-label">Ideal For</label>
                            <Autocomplete
                                multiple
                                options={configData.idealFor}
                                value={formik.values.idealFor || []}
                                onChange={(event, value) => formik.setFieldValue("idealFor", value)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="Select Ideal For"
                                    />
                                )}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 12 }}>
                            <label htmlFor="amenities" className="form-label">Amenities</label>
                            <Autocomplete
                                multiple
                                options={configData.amenities}
                                value={formik.values.amenities || []}
                                onChange={(event, value) => formik.setFieldValue("amenities", value)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="Select Amenities"
                                    />
                                )}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                            <label htmlFor="floorPlan" className="form-label">Floor Plan</label>
                            <TextField
                                type="file"
                                name="floorPlan"
                                id="floorPlan"
                                onChange={(event) => {
                                    const target = event.target as HTMLInputElement;
                                    const file = target.files?.[0];
                                    formik.setFieldValue("floorPlan", file);
                                }}
                                variant="outlined"
                                fullWidth
                            />
                            {formik.values.floorPlan ? (
                                <div style={{ marginTop: "10px" }}>
                                    <img
                                        src={id ? formik.values.floorPlan : URL.createObjectURL(formik.values.floorPlan)}
                                        alt="floorplan"
                                        style={{ maxWidth: "200px", maxHeight: "200px" }}
                                    />
                                </div>
                            ) : null}
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                            <label htmlFor="eceDocument" className="form-label">ECE Documents</label>
                            <TextField
                                type="file"
                                name="eceDocument"
                                id="eceDocument"
                                onChange={(event) => {
                                    const target = event.target as HTMLInputElement;
                                    const file = target.files?.[0];
                                    formik.setFieldValue("eceDocument", file);
                                }}
                                inputProps={{ accept: ".pdf,.doc,.docx" }}
                                variant="outlined"
                                fullWidth
                            />
                            {formik.values.eceDocument ? (
                                <div style={{ marginTop: "10px" }}>
                                    {typeof formik.values.eceDocument === "string" ? (
                                        <a
                                            href={formik.values.eceDocument}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ color: "#1976d2", textDecoration: "underline" }}
                                        >
                                            View Uploaded Document
                                        </a>
                                    ) : (
                                        <a
                                            href={URL.createObjectURL(formik.values.eceDocument)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ color: "#1976d2", textDecoration: "underline" }}
                                        >
                                            {formik.values.eceDocument.name}
                                        </a>
                                    )}
                                </div>
                            ) : null}
                        </Grid>
                    </>
                );

            case 'Agricultural':
                return (
                    <>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <label htmlFor="areaValue" className="form-label">Area Value</label>
                            <TextField
                                fullWidth
                                name="areaValue"
                                type="number"
                                value={formik.values.area?.value ?? ''}
                                onChange={(e) => formik.setFieldValue("area", {
                                    ...formik.values.area,
                                    value: e.target.value === '' ? '' : Number(e.target.value)
                                })}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                            <label htmlFor="areaUnit" className="form-label">Area Unit</label>
                            <Autocomplete
                                options={configData.areaUnits}
                                value={formik.values.area?.unit || "Acres"}
                                onChange={(event, value) => formik.setFieldValue("area", {
                                    ...formik.values.area,
                                    unit: value
                                })}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="Select Area Unit"
                                    />
                                )}
                            />
                        </Grid>
                    </>
                );

            default:
                return null;
        }
    };

    return (
        <Box paddingY={4}>
            <h3>{actualId ? 'Edit' : 'Add'} {propertyType} Property</h3>
            <form onSubmit={formik.handleSubmit} encType="multipart/form-data">
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <label htmlFor="title" className="form-label">Title</label>
                        <TextField
                            fullWidth
                            name="title"
                            placeholder="Property Title"
                            value={formik.values.title}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.title && Boolean(formik.errors.title)}
                            helperText={formik.touched.title && formik.errors.title}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }}>
                        <label htmlFor="price" className="form-label">Price (₹)</label>
                        <TextField
                            fullWidth
                            name="price"
                            placeholder="Price"
                            value={formik.values.price}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.price && Boolean(formik.errors.price)}
                            helperText={formik.touched.price && formik.errors.price}
                        />
                    </Grid>

                    {renderPropertyTypeFields()}

                    <Grid size={{ xs: 12, md: 12 }}>
                        <label htmlFor="address" className="form-label">Address</label>
                        <TextField
                            fullWidth
                            name="address"
                            placeholder="Property Address"
                            value={formik.values.address}
                            onChange={handleSearchInput}
                            onBlur={formik.handleBlur}
                            error={formik.touched.address && Boolean(formik.errors.address)}
                            helperText={formik.touched.address && formik.errors.address}
                        />
                        {suggestions.length > 0 && (
                            <ul style={{ border: '1px solid #ccc', maxHeight: '200px', overflowY: 'auto' }}>
                                {suggestions.map((suggestion: any, index: any) => (
                                    <li
                                        key={index}
                                        onClick={() => handleSuggestionClick(suggestion)}
                                        style={{ padding: '8px', cursor: 'pointer' }}
                                    >
                                        {suggestion?.display_name}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }}>
                        <label htmlFor="postedBy" className="form-label">Posted By</label>
                        <Autocomplete
                            options={propertyType === 'Agricultural' ? ['Owner', 'Dealer', 'Agent'] : ['Owner', 'Dealer', 'Builder']}
                            value={formik.values.postedBy}
                            onChange={(event, value) => formik.setFieldValue("postedBy", value)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Select Posted By"
                                />
                            )}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }}>
                        <label htmlFor="propertyStatus" className="form-label">Property Status</label>
                        <Autocomplete
                            options={
                                propertyType === 'Agricultural'
                                    ? ['For Sale', 'Sold Out', 'For Rent', 'Rented']
                                    : propertyType === 'Residential'
                                        ? ['For Rent', 'Rented', 'For Sale', 'Sold']
                                        : ['For Rent', 'Rented', 'For Sale', 'Sold']
                            } value={formik.values.propertyStatus}
                            onChange={(event, value) => formik.setFieldValue("propertyStatus", value)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Select Property Status"
                                />
                            )}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }}>
                        <label htmlFor="status" className="form-label">Status</label>
                        <Autocomplete
                            options={['Active', 'Inactive', 'Sold']}
                            value={formik.values.status}
                            onChange={(event, value) => formik.setFieldValue("status", value)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Select Status"
                                />
                            )}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }} sx={{ marginTop: "25px" }}>
                        <label htmlFor="featured" className="form-label">Featured</label>
                        <FormControlLabel
                            sx={{ marginLeft: "4px" }}
                            control={
                                <Switch
                                    name="featured"
                                    checked={formik.values.featured}
                                    onChange={(event) => {
                                        formik.setFieldValue("featured", event.target.checked);
                                    }}
                                    onBlur={formik.handleBlur}
                                    color="primary"
                                />
                            }
                            label={formik.values.featured ? "Yes" : "No"}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, md: 12 }}>
                        <label htmlFor="images" className="form-label">Add Photos</label>
                        <Box
                            {...getRootProps()}
                            sx={{
                                border: "2px dashed #ccc",
                                padding: 2,
                                textAlign: "center",
                                cursor: "pointer",
                                backgroundColor: isDragActive ? "#f0f0f0" : "inherit",
                            }}
                        >
                            <input {...getInputProps()} />
                            {isDragActive ? (
                                <Typography>Drop the images here...</Typography>
                            ) : (
                                <Typography>
                                    Drag & drop some images here, or click to select files
                                </Typography>
                            )}
                        </Box>
                        {formik.errors.images && (
                            <Typography color="error">{formik.errors.images}</Typography>
                        )}
                    </Grid>

                    <Grid size={{ xs: 12, md: 12 }}>
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleDragEnd}
                            onDragStart={({ active }) => setActiveId(active.id)}
                        >
                            <SortableContext
                                items={formik.values.images.map((image: any) => image.preview)}
                                strategy={verticalListSortingStrategy}
                            >
                                <Grid container spacing={2}>
                                    {formik.values.images.map((image: any, index: any) => (
                                        <Grid size={{ xs: 12, md: 4 }} key={image.preview}>
                                            <SortableItem
                                                id={image.preview}
                                                paramId={actualId}
                                                index={index}
                                                image={image}
                                                onRemove={() => removeImage(index)}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            </SortableContext>
                            <DragOverlay>
                                {activeId ? (
                                    <img
                                        src={activeId}
                                        alt="drag-preview"
                                        style={{ width: "100px", height: "100px" }}
                                    />
                                ) : null}
                            </DragOverlay>
                        </DndContext>
                    </Grid>

                    <Grid size={{ xs: 12, md: 12 }}>
                        <MapField
                            label="Pick a Location"
                            value={formik.values.coordinates ? {
                                lat: formik.values.coordinates.latitude,
                                lng: formik.values.coordinates.longitude
                            } : { lat: 28.6139, lng: 77.2090 }}
                            address={formik.values.address}
                            onChange={handleLocationChange}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, md: 12 }}>
                        <label htmlFor="description" className="form-label">Description</label>
                        <TextEditor
                            value={formik.values.description}
                            setFieldWithEditor={(newValue: any) =>
                                formik.setFieldValue("description", newValue)
                            }
                        />
                        {formik.touched.description && Boolean(formik.errors.description) && (
                            <Typography color="error">{formik.errors.description}</Typography>
                        )}
                    </Grid>

                    <Grid
                        size={{ xs: 12, md: 12 }}
                        sx={{ display: "flex", justifyContent: "flex-end" }}
                    >
                        <Button component={Link} to={`/properties?country=${country}`} color="secondary">
                            Cancel
                        </Button>
                        <Button type="submit" color="primary" variant="contained">
                            {actualId ? "Save" : "Add"} Property
                        </Button>
                    </Grid>
                </Grid>
            </form>
            <ToastContainer position="top-right" autoClose={3000} />
        </Box>
    );
};

export default AddPropertyIndia;